import type { SupportedLocale } from "@/lib/i18n";
import type { Product } from "@/data/products";
import {
  getProductSeoScale,
  getSeoCategoryLabel,
  resolveSeoProductCategory,
} from "@/data/seoProductCategory";

type SeoTemplates = {
  title: string;
  titleNoScale: string;
  description: string;
  descriptionNoScale: string;
};

const SEO_TEMPLATES: Partial<Record<SupportedLocale, SeoTemplates>> & {
  en: SeoTemplates;
} = {
  en: {
    title: "{{brand}} {{name}} ({{number}}) - {{scale}} {{category}}",
    titleNoScale: "{{brand}} {{name}} ({{number}}) - {{category}}",
    description:
      "Discover the {{brand}} {{name}} ({{number}}), a {{scale}} {{category}} built from {{pieces}} pieces. View specifications, dimensions, features and product images.",
    descriptionNoScale:
      "Discover the {{brand}} {{name}} ({{number}}), a {{category}} built from {{pieces}} pieces. View specifications, dimensions, features and product images.",
  },
  pl: {
    title: "{{brand}} {{name}} ({{number}}) - {{category}} {{scale}}",
    titleNoScale: "{{brand}} {{name}} ({{number}}) - {{category}}",
    description:
      "Poznaj {{brand}} {{name}} ({{number}}) w skali {{scale}}. Zobacz specyfikację, wymiary, funkcje, liczbę elementów oraz zdjęcia produktu.",
    descriptionNoScale:
      "Poznaj {{brand}} {{name}} ({{number}}). Zobacz specyfikację, wymiary, funkcje, liczbę elementów oraz zdjęcia produktu.",
  },
  de: {
    title: "{{brand}} {{name}} ({{number}}) - {{category}} {{scale}}",
    titleNoScale: "{{brand}} {{name}} ({{number}}) - {{category}}",
    description:
      "Entdecken Sie das {{brand}} {{name}} ({{number}}) im Maßstab {{scale}} mit {{pieces}} Teilen. Technische Daten, Abmessungen, Funktionen und Produktbilder.",
    descriptionNoScale:
      "Entdecken Sie das {{brand}} {{name}} ({{number}}) mit {{pieces}} Teilen. Technische Daten, Abmessungen, Funktionen und Produktbilder.",
  },
  fr: {
    title: "{{brand}} {{name}} ({{number}}) - {{category}} {{scale}}",
    titleNoScale: "{{brand}} {{name}} ({{number}}) - {{category}}",
    description:
      "Découvrez le {{brand}} {{name}} ({{number}}) à l'échelle {{scale}} avec {{pieces}} pièces. Consultez les spécifications, dimensions, fonctionnalités et photos du produit.",
    descriptionNoScale:
      "Découvrez le {{brand}} {{name}} ({{number}}) avec {{pieces}} pièces. Consultez les spécifications, dimensions, fonctionnalités et photos du produit.",
  },
  es: {
    title: "{{brand}} {{name}} ({{number}}) - {{category}} {{scale}}",
    titleNoScale: "{{brand}} {{name}} ({{number}}) - {{category}}",
    description:
      "Descubre el {{brand}} {{name}} ({{number}}) a escala {{scale}} con {{pieces}} piezas. Consulta las especificaciones, dimensiones, características e imágenes del producto.",
    descriptionNoScale:
      "Descubre el {{brand}} {{name}} ({{number}}) con {{pieces}} piezas. Consulta las especificaciones, dimensiones, características e imágenes del producto.",
  },
  nl: {
    title: "{{brand}} {{name}} ({{number}}) - {{category}} {{scale}}",
    titleNoScale: "{{brand}} {{name}} ({{number}}) - {{category}}",
    description:
      "Ontdek de {{brand}} {{name}} ({{number}}) op schaal {{scale}} met {{pieces}} onderdelen. Bekijk specificaties, afmetingen, kenmerken en productafbeeldingen.",
    descriptionNoScale:
      "Ontdek de {{brand}} {{name}} ({{number}}) met {{pieces}} onderdelen. Bekijk specificaties, afmetingen, kenmerken en productafbeeldingen.",
  },
  zh: {
    title: "{{brand}} {{name}}（{{number}}）- {{scale}}{{category}}",
    titleNoScale: "{{brand}} {{name}}（{{number}}）- {{category}}",
    description:
      "探索 {{brand}} {{name}}（{{number}}）{{scale}}拼搭模型，包含{{pieces}}块积木。查看产品规格、尺寸、特色功能及官方图片。",
    descriptionNoScale:
      "探索 {{brand}} {{name}}（{{number}}）拼搭模型，包含{{pieces}}块积木。查看产品规格、尺寸、特色功能及官方图片。",
  },
};

function applyTemplate(
  template: string,
  vars: Record<string, string>
): string {
  return template
    .replace(/\{\{(\w+)\}\}/g, (_, key: string) => vars[key] ?? "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
}

export function buildProductSeoTitle(
  product: Product,
  locale: SupportedLocale
): string {
  const brand = product.brand || "12GO";
  const name = product.name?.trim() || `${brand} ${product.numbering}`;
  const scale = getProductSeoScale(product);
  const category = getSeoCategoryLabel(resolveSeoProductCategory(product), locale);
  const templates = SEO_TEMPLATES[locale] ?? SEO_TEMPLATES.en;
  const template = scale ? templates.title : templates.titleNoScale;

  return applyTemplate(template, {
    brand,
    name,
    number: product.numbering,
    scale,
    category,
    pieces: String(product.pieces),
  });
}

export function buildProductSeoDescription(
  product: Product,
  locale: SupportedLocale
): string {
  const brand = product.brand || "12GO";
  const name = product.name?.trim() || `${brand} ${product.numbering}`;
  const scale = getProductSeoScale(product);
  const category = getSeoCategoryLabel(resolveSeoProductCategory(product), locale);
  const templates = SEO_TEMPLATES[locale] ?? SEO_TEMPLATES.en;
  const template = scale ? templates.description : templates.descriptionNoScale;

  return applyTemplate(template, {
    brand,
    name,
    number: product.numbering,
    scale,
    category,
    pieces: String(product.pieces),
  });
}
